import { useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, MessageCircle, Mail, CheckCircle2, Undo2, Save } from "lucide-react";
import { gmailCompose } from "../../lib/gmail";

const STATUS_LABEL = {
  nuevo: "Nuevo",
  presupuestado: "Presupuestado",
  pagado: "Pagado",
  enviado: "Enviado",
  cancelado: "Cancelado",
};

const eur = (n) =>
  n === null || n === undefined || n === "" ? "—" : new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(Number(n));

function quoteMessage(order, paymentText) {
  const lines = [
    `Hola ${order.name}, soy de Maggie Records. ¡Gracias por tu pedido ${order.code}!`,
    "",
    ...order.items.map((it) => `• ${it.artist} — ${it.title} (${eur(it.price)})`),
    "",
    `Discos: ${eur(order.subtotal)}`,
    `Envío a ${order.country}: ${eur(order.shipping)}`,
    `Total: ${eur(order.total)}`,
    "",
    (paymentText || "").trim() || "Te indico ahora cómo hacer el pago.",
  ];
  return lines.join("\n");
}

function whatsappUrl(phone, text) {
  const digits = (phone || "").replace(/[^\d]/g, "").replace(/^00/, "");
  return digits.length >= 8 ? `https://wa.me/${digits}?text=${encodeURIComponent(text)}` : null;
}

export default function AdminOrders({ orders, client, paymentText, onChanged }) {
  if (!orders) return <p className="text-sm text-muted-foreground">Cargando pedidos…</p>;
  return (
    <div data-testid="admin-orders-tab">
      <div className="mb-6 border-b border-border pb-4">
        <h2 className="text-2xl">Pedidos</h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Pedidos hechos desde el carrito de la web. Pon el envío y envía el total por WhatsApp o email.
          Cuando el cliente pague, pulsa «Pedido pagado: finalizar»: los discos desaparecen de la web y se retiran de Discogs.
        </p>
        <div className="flex flex-wrap gap-4 mt-3 vmr-tiny text-muted-foreground">
          {Object.entries(orders.counts || {}).map(([k, v]) => (
            <span key={k}><strong className="text-foreground">{v}</strong> {STATUS_LABEL[k] || k}</span>
          ))}
        </div>
        {!paymentText && (
          <p className="text-xs text-amber-700 mt-3">
            Añade tus instrucciones de pago en <strong>Textos → order.payment</strong> y se incluirán en los mensajes.
          </p>
        )}
      </div>
      {orders.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Aún no hay pedidos.</p>
      ) : (
        <div className="space-y-3">
          {orders.items.map((o) => (
            <OrderCard key={o.id} order={o} client={client} paymentText={paymentText} statuses={orders.statuses} onChanged={onChanged} />
          ))}
        </div>
      )}
    </div>
  );
}

function OrderCard({ order, client, paymentText, statuses, onChanged }) {
  const [open, setOpen] = useState(order.status === "nuevo");
  const [status, setStatus] = useState(order.status);
  const [shipping, setShipping] = useState(order.shipping ?? "");
  const [total, setTotal] = useState(order.total ?? "");
  const [totalTouched, setTotalTouched] = useState(order.total !== null && order.total !== undefined);
  const [notes, setNotes] = useState(order.admin_notes || "");
  const [saving, setSaving] = useState(false);

  // Total follows subtotal + shipping until edited by hand (e.g. for a discount).
  const computedTotal = shipping === "" ? "" : Math.round((Number(order.subtotal) + Number(shipping)) * 100) / 100;
  const effectiveTotal = totalTouched ? total : computedTotal;
  const draft = { ...order, shipping: shipping === "" ? null : Number(shipping), total: effectiveTotal === "" ? null : Number(effectiveTotal) };
  const canQuote = draft.shipping !== null && draft.total !== null;
  const message = quoteMessage(draft, paymentText);
  const wa = whatsappUrl(order.phone, message);
  const mailto = gmailCompose({ to: order.email, subject: `Tu pedido ${order.code} en Maggie Records`, body: message });

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      await client.put(`/admin/orders/${order.id}`, {
        status,
        shipping: shipping === "" ? null : Number(shipping),
        total: effectiveTotal === "" ? null : Number(effectiveTotal),
        admin_notes: notes,
        ...extra,
      });
      toast.success(`Pedido ${order.code} guardado`);
      await onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const sendQuote = async (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
    // Sending the quote moves a new order to "presupuestado" and stores the amounts.
    if (status === "nuevo") setStatus("presupuestado");
    await save(status === "nuevo" ? { status: "presupuestado" } : {});
  };

  const finalize = async () => {
    if (!window.confirm(
      `¿Marcar ${order.code} como PAGADO?\n\n• Los ${order.items.length} discos desaparecen de la web.\n• Se retiran de la venta en Discogs (pasan a borrador; se puede deshacer cancelando el pedido).`,
    )) return;
    try {
      await client.post(`/admin/orders/${order.id}/finalize`);
      toast.success("Pedido finalizado. Los discos se retirarán de Discogs en 1-2 minutos.", { duration: 10000 });
      await onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo finalizar");
    }
  };

  const cancel = async () => {
    if (!window.confirm(`¿Cancelar ${order.code}? Los discos vuelven a mostrarse en la web y, si ya se retiraron de Discogs, se vuelven a poner a la venta.`)) return;
    try {
      await client.post(`/admin/orders/${order.id}/cancel`);
      toast.success("Pedido cancelado. Discos de nuevo a la venta.");
      await onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cancelar");
    }
  };

  const discogsLabel = (listingId) => {
    const a = order.discogs?.[listingId];
    if (!a) return null;
    if (a.action === "draft") {
      if (a.status === "done") return <span className="text-green-700">Retirado de Discogs ✓</span>;
      if (a.status === "pending") return <span className="text-muted-foreground">Retirando de Discogs…</span>;
      return <span className="text-red-700" title={a.error || ""}>No se pudo retirar de Discogs: {a.error}</span>;
    }
    if (a.status === "done") return <span className="text-muted-foreground">De nuevo a la venta en Discogs</span>;
    if (a.status === "pending") return <span className="text-muted-foreground">Volviendo a poner a la venta…</span>;
    return <span className="text-red-700">Error al reponer en Discogs: {a.error}</span>;
  };

  return (
    <div className="border border-border" data-testid={`order-${order.code}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex flex-wrap items-center justify-between gap-3 p-4 text-left hover:bg-[hsl(var(--muted))]">
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <strong>{order.code}</strong>
          <span className="text-muted-foreground text-xs">{new Date(order.created_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}</span>
          <span>{order.name}</span>
          <span className="text-muted-foreground text-xs">{order.country}</span>
          <span className="text-xs">{order.items.length} {order.items.length === 1 ? "disco" : "discos"}</span>
        </span>
        <span className="flex items-center gap-3">
          <span className={`vmr-tiny uppercase tracking-wider px-2 py-0.5 border ${order.status === "nuevo" ? "border-[hsl(var(--accent))] text-[hsl(var(--accent))]" : "border-border"}`}>
            {STATUS_LABEL[order.status] || order.status}
          </span>
          <span className="text-sm font-medium tabular-nums">{order.total !== null ? eur(order.total) : eur(order.subtotal)}</span>
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>

      {open && (
        <div className="border-t border-border p-5 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <h4 className="vmr-tiny text-muted-foreground mb-2">Discos</h4>
            <ul className="divide-y divide-border">
              {order.items.map((it) => (
                <li key={it.listing_id} className="flex flex-wrap items-center gap-3 py-2">
                  <img src={it.thumb || "/brand/cover-placeholder.png"} alt="" className="w-10 h-10 object-cover bg-[hsl(var(--muted))]" />
                  <a href={`/catalogo/${it.slug}`} target="_blank" rel="noreferrer" className="flex-1 min-w-0 text-sm hover:underline">
                    <span className="block text-xs text-muted-foreground truncate">{it.artist}</span>
                    <span className="block truncate">{it.title}</span>
                  </a>
                  {it.discogs_uri && <a href={it.discogs_uri} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground underline">Discogs</a>}
                  <span className="text-sm tabular-nums">{eur(it.price)}</span>
                  {discogsLabel(it.listing_id) && <span className="basis-full text-xs pl-[52px]">{discogsLabel(it.listing_id)}</span>}
                </li>
              ))}
            </ul>
            <p className="text-sm mt-3 flex justify-between"><span className="text-muted-foreground">Subtotal discos</span><span className="tabular-nums">{eur(order.subtotal)}</span></p>

            <h4 className="vmr-tiny text-muted-foreground mt-6 mb-2">Cliente</h4>
            <p className="text-sm leading-relaxed">
              <strong>{order.name}</strong><br />
              <a href={gmailCompose({ to: order.email })} target="_blank" rel="noreferrer" className="underline">{order.email}</a>{order.phone ? ` · ${order.phone}` : ""}<br />
              {[order.address, [order.postal_code, order.city].filter(Boolean).join(" "), order.country].filter(Boolean).join(", ")}<br />
              <span className="text-muted-foreground">Prefiere: {order.contact_pref === "whatsapp" ? "WhatsApp" : "email"}</span>
            </p>
            {order.notes && <p className="text-sm mt-3 p-3 bg-[hsl(var(--muted))] whitespace-pre-line">{order.notes}</p>}
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="vmr-tiny text-muted-foreground">Envío (€)</span>
                <input type="number" step="0.01" min="0" value={shipping} onChange={(e) => setShipping(e.target.value)} placeholder="ej. 22" className="w-full bg-transparent border border-border px-3 py-2 text-sm mt-1" data-testid="order-shipping" />
              </label>
              <label className="block">
                <span className="vmr-tiny text-muted-foreground">Total (€)</span>
                <input type="number" step="0.01" min="0" value={effectiveTotal} onChange={(e) => { setTotalTouched(true); setTotal(e.target.value); }} className="w-full bg-transparent border border-border px-3 py-2 text-sm mt-1" data-testid="order-total" />
              </label>
            </div>
            <label className="block">
              <span className="vmr-tiny text-muted-foreground">Estado</span>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full bg-transparent border border-border px-3 py-2 text-sm mt-1" data-testid="order-status">
                {(statuses || Object.keys(STATUS_LABEL)).map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="vmr-tiny text-muted-foreground">Notas internas (seguimiento, acuerdos…)</span>
              <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full bg-transparent border border-border px-3 py-2 text-sm mt-1" />
            </label>
            <button disabled={saving} onClick={() => save()} className="w-full inline-flex items-center justify-center gap-2 border border-foreground py-2 vmr-tiny disabled:opacity-40" data-testid="order-save">
              <Save size={12} /> Guardar
            </button>

            <div className="border-t border-border pt-4 space-y-2">
              <p className="vmr-tiny text-muted-foreground">Enviar el total al cliente</p>
              {!canQuote && <p className="text-xs text-muted-foreground">Pon el envío para generar el mensaje con el total.</p>}
              <div className="grid grid-cols-2 gap-2">
                <button
                  disabled={!canQuote || !wa}
                  onClick={() => sendQuote(wa)}
                  title={!wa ? "El cliente no dejó teléfono" : ""}
                  className="inline-flex items-center justify-center gap-2 bg-foreground text-background py-2.5 vmr-tiny disabled:opacity-30"
                  data-testid="order-whatsapp"
                >
                  <MessageCircle size={13} /> WhatsApp
                </button>
                <button
                  disabled={!canQuote}
                  onClick={() => sendQuote(mailto)}
                  className="inline-flex items-center justify-center gap-2 border border-foreground py-2.5 vmr-tiny disabled:opacity-30"
                  data-testid="order-email"
                >
                  <Mail size={13} /> Email
                </button>
              </div>
              {canQuote && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground">Ver mensaje</summary>
                  <pre className="mt-2 p-3 bg-[hsl(var(--muted))] whitespace-pre-wrap font-sans">{message}</pre>
                </details>
              )}
            </div>

            <div className="border-t border-border pt-4 space-y-2">
              {order.status !== "pagado" && order.status !== "enviado" && order.status !== "cancelado" && (
                <button onClick={finalize} className="w-full inline-flex items-center justify-center gap-2 bg-[hsl(var(--accent))] text-white py-2.5 vmr-tiny" data-testid="order-finalize">
                  <CheckCircle2 size={13} /> Pedido pagado: finalizar
                </button>
              )}
              {order.status !== "cancelado" && order.status !== "enviado" && (
                <button onClick={cancel} className="w-full inline-flex items-center justify-center gap-2 border border-border py-2 vmr-tiny text-muted-foreground hover:text-foreground hover:border-foreground" data-testid="order-cancel">
                  <Undo2 size={12} /> Cancelar pedido (volver a poner a la venta)
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
